import React, { useContext, useEffect, useState } from "react";
import { Plus } from "lucide-react";

import AuthContext from "../../contexts/AuthCtx";
import { ClipLoader } from "react-spinners";
import LetterDialog, { letterModalBodyClass } from "../UX/LetterDialog";
import { FieldLabel, OnboardingInput, OnboardingSelect } from "../Onboarding/OnboardingFields";
import { api } from "../../api/api";
import customStamp from "../../assets/icons/post-stamp.svg";

const EMPTY_FORM = {
  title: "",
  firstName: "",
  lastName: "",
  country: "",
  stAndName: "",
  postalCode: "",
  city: "",
  locality: "",
  address: "",
};

function RecipientModal({
  open = true,
  isEditing,
  recipientId,
  letterId,
  onSubmit,
  onClose,
  onBuyStamps,
  onDelete,
}) {
  const AuthCtx = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const availableStamps = AuthCtx.totalStamps - AuthCtx.usedStamps;
  const stampsAfterAdd = availableStamps - (isEditing ? 0 : 1);
  const canAdd = isEditing || availableStamps > 0;

  useEffect(() => {
    if (!open) return;

    if (!isEditing) {
      setFormData(EMPTY_FORM);
      setIsLoading(false);
      return;
    }

    if (!letterId || !recipientId) return;

    setIsLoading(true);
    api
      .get(`letter/getRecipient/${letterId}/${recipientId}`)
      .then(({ data }) => {
        setFormData(data.recipient);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  }, [open, isEditing, letterId, recipientId]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canAdd) return;
    onSubmit(formData);
  };

  const inputClass =
    "w-full rounded-md border border-greyLight bg-white py-3 px-4 font-josefin text-secondaryText placeholder:text-textMuted outline-none focus:border-brandPurple transition duration-200";

  return (
    <LetterDialog
      open={open}
      onClose={onClose}
      maxWidth="max-w-2xl"
      scrollable
      centered
      animationVariant="slideUp"
      title={
        isEditing
          ? "Modifier le destinataire"
          : "À qui cette lettre est-elle destinée ?"
      }
    >
      <form onSubmit={handleSubmit}>
        {!isEditing && (
          <p className={`${letterModalBodyClass} mb-4 text-center text-textMuted`}>
            Tu pourras la laisser s&apos;envoyer automatiquement après ton
            décès, ou choisir de l&apos;envoyer manuellement au moment de ton
            choix.
          </p>
        )}
        <p className={`${letterModalBodyClass} mb-8 text-center text-textMuted`}>
          Ces informations restent strictement confidentielles
          <br />
          et ne servent qu&apos;à l&apos;envoi postal de ta lettre.
        </p>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <ClipLoader color="#632C5F" size={32} />
          </div>
        ) : (
          <>
            <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <FieldLabel htmlFor="lastName" required>
                  Nom
                </FieldLabel>
                <OnboardingInput
                  id="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Nom"
                  required
                />
              </div>
              <div>
                <FieldLabel htmlFor="firstName" required>
                  Prénom
                </FieldLabel>
                <OnboardingInput
                  id="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Prénom"
                  required
                />
              </div>
            </div>

            <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <FieldLabel htmlFor="title" required>
                  Titre
                </FieldLabel>
                <OnboardingSelect
                  id="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Titre"
                >
                  <option value="male">Monsieur</option>
                  <option value="female">Madame</option>
                </OnboardingSelect>
              </div>
              <div>
                <FieldLabel htmlFor="country" required>
                  Pays de résidence
                </FieldLabel>
                <OnboardingInput
                  id="country"
                  value={formData.country}
                  onChange={handleChange}
                  placeholder="Pays de résidence"
                  required
                />
              </div>
            </div>

            <div className="mb-5">
              <FieldLabel htmlFor="stAndName" required>
                Numéro et libellé de voie
              </FieldLabel>
              <OnboardingInput
                id="stAndName"
                value={formData.stAndName}
                onChange={handleChange}
                placeholder="Numéro et libellé de voie"
                required
              />
            </div>

            <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-[2fr_3fr]">
              <div>
                <FieldLabel htmlFor="postalCode" required>
                  Code Postal
                </FieldLabel>
                <OnboardingInput
                  id="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  placeholder="Code Postal"
                  required
                />
              </div>
              <div>
                <FieldLabel htmlFor="city" required>
                  Ville
                </FieldLabel>
                <OnboardingInput
                  id="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Ville"
                  required
                />
              </div>
            </div>

            <div className="mb-5">
              <FieldLabel htmlFor="locality">Lieu-dit ou Boite Postale</FieldLabel>
              <input
                id="locality"
                type="text"
                value={formData.locality}
                onChange={handleChange}
                placeholder="Lieu-dit ou Boite Postale"
                className={inputClass}
              />
            </div>

            <div className="mb-8">
              <FieldLabel htmlFor="address">
                Complément d&apos;adresse (bâtiment, appartement, étage, chez,…)
              </FieldLabel>
              <input
                id="address"
                type="text"
                value={formData.address}
                onChange={handleChange}
                placeholder="Complément d'adresse"
                className={inputClass}
              />
            </div>

            {!isEditing && (
              <div className="mb-8 flex flex-col items-center gap-2">
                <div className="flex items-center gap-2">
                  <img src={customStamp} width={28} height={28} alt="" />
                  <span className="font-josefin text-xl text-secondaryText">
                    {availableStamps}
                  </span>
                  <button
                    type="button"
                    onClick={onBuyStamps}
                    aria-label="Acheter des timbres"
                    className="flex h-6 w-6 items-center justify-center rounded bg-brandPurple transition hover:bg-primaryText"
                  >
                    <Plus size={14} className="text-white" strokeWidth={2.5} />
                  </button>
                </div>
                {canAdd ? (
                  <p className="font-josefin text-sm text-textMuted">
                    Il te restera {stampsAfterAdd} timbre
                    {stampsAfterAdd !== 1 ? "s" : ""}.
                  </p>
                ) : (
                  <p className="font-josefin text-sm text-textMuted">
                    Il te faut un timbre pour ajouter un destinataire.
                  </p>
                )}
              </div>
            )}

            <div className="flex flex-col items-center gap-3">
              <button
                type="submit"
                disabled={!canAdd}
                className={`w-full max-w-xs rounded-md py-3 font-josefin text-sm font-medium text-white transition sm:px-12 ${
                  canAdd
                    ? "bg-sageDark hover:bg-modalSage"
                    : "cursor-not-allowed bg-modalDisabled"
                }`}
              >
                {isEditing ? "Enregistrer" : "Ajouter ce destinataire"}
              </button>

              {isEditing && onDelete && (
                <button
                  type="button"
                  onClick={onDelete}
                  className="font-josefin text-sm text-modalRed underline-offset-2 hover:underline"
                >
                  Supprimer ce destinataire
                </button>
              )}
            </div>
          </>
        )}
      </form>
    </LetterDialog>
  );
}

export default RecipientModal;
